import { useNavigate } from 'react-router-dom';
import { classImageList } from '../../../constants/classImages';
import { teacherImageList } from '../../../constants/teacherImages';
import { useQuery } from '@tanstack/react-query';

const ClassesSection = () => {
  const navigate = useNavigate();
  const API_URL = import.meta.env.VITE_API_URL;
  const { data } = useQuery({
    queryKey: ['classList'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/bbs/ajax.get_class_list.php?page=1`);
      return res.json();
    }
  });

  const items = (data?.data.list ?? []).map((item: any) => ({
    ...item,
    file: classImageList[item.name] || null,
    teacherProfiles: item.teachers?.map((name: string) => ({
      name,
      image: teacherImageList[name] || null
    }))
  }));

  return (
    <section id="classes" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
            인기 <span className="text-purple-600">AI 수업</span>
          </h2>
          <p className="md:text-xl text-lg text-gray-600 max-w-3xl mx-auto">
            아이들이 가장 좋아하는 AI 라이브 수업들을 만나보세요
          </p>
        </div>
        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          data-product-shop
        >
          {items.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="relative w-full h-48 bg-gray-200 flex items-center justify-center">
                <span className="text-2xl font-bold text-gray-500">
                  Coming Soon
                </span>
              </div>
              <div className="p-6">
                <div className="flex flex-wrap gap-2 mb-3">
                  <span className="bg-gray-100 px-2 py-1 rounded-full text-xs">
                    준비중
                  </span>
                </div>
                <h3 className="text-xl font-bold text-gray-400 mb-3">
                  새로운 클래스 준비중입니다
                </h3>
                <p className="text-gray-400 mb-4">
                  곧 멋진 클래스가 추가될 예정입니다.
                </p>
                <div className="flex items-center justify-between">
                  <div className="text-gray-400">-</div>
                  <button
                    disabled
                    className="bg-gray-300 text-white px-6 py-2 rounded-full cursor-not-allowed"
                  >
                    Coming Soon
                  </button>
                </div>
              </div>
            </div>
          ) : (
            items.map((item: any) => (
              <div
                key={item.id}
                className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden"
              >
                <div className="relative">
                  {item.file ? (
                    <img
                      src={item.file}
                      alt={item.name}
                      className="w-full h-48 object-cover"
                    />
                  ) : (
                    <p className="text-2xl font-bold text-gray-500 h-48 flex items-center justify-center bg-gray-200">
                      No Image
                    </p>
                  )}
                  <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full">
                    <span className="text-sm font-bold text-purple-600">
                      {item.age}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex flex-wrap gap-2 mb-3">
                    {item.categories?.map((category: string, index: number) => (
                      <span
                        key={index}
                        className="bg-blue-100 text-blue-600 px-2 py-1 rounded-full text-xs font-medium"
                      >
                        {category}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center gap-4 mb-3">
                    {/* 프로필 이미지 */}
                    <div className="relative inline-block flex-shrink-0">
                      {item.teacherProfiles?.[0]?.image ? (
                        <img
                          src={item.teacherProfiles?.[0]?.image}
                          alt={item.teacherProfiles?.[0]?.name}
                          className="w-20 h-20 rounded-full object-cover border-4 border-blue-100"
                        />
                      ) : (
                        <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center border-4 border-gray-100">
                          <i className="ri-user-line text-2xl text-gray-400"></i>
                        </div>
                      )}
                      <div className="absolute bottom-0 right-2 bg-green-500 w-4 h-4 rounded-full border-2 border-white"></div>
                    </div>

                    {/* 제목 */}
                    <h3 className="text-xl font-bold text-gray-900 h-[60px] min-w-0 flex-1">
                      <span className="block truncate">
                        {item.course}
                      </span>
                      <span className="block text-sm font-medium text-gray-500 truncate">
                        강사 : {item.teachers.join(', ')}
                      </span>
                    </h3>
                  </div>
                  <p className="text-gray-600 leading-relaxed h-[25px] truncate">
                    {item.name}
                  </p>
                  <p className="text-gray-600 mb-4 leading-relaxed h-[25px] truncate">
                    {item.description}
                  </p>
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-1">
                        <i className="ri-time-line"></i>
                        <span>{item.time}분</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <i className="ri-group-line"></i>
                        <span>{item.capacity}명</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="text-2xl font-bold text-purple-600">
                      <span className="text-sm font-bold block">
                        1회 수업료
                      </span>
                      {item.salePrice.toLocaleString()}원
                      {item.originalPrice != item.salePrice && (
                        <span className="text-sm text-red-500 line-through ml-2">
                          {Number(item.originalPrice).toLocaleString()}원
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => navigate(`/class/${item.id}`)}
                      className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-2 rounded-full hover:from-blue-600 hover:to-purple-700 transition-all whitespace-nowrap cursor-pointer"
                    >
                      클래스 보기
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
        <div className="text-center mt-16">
          <button
            onClick={() => navigate('/classes')}
            className="bg-gradient-to-r from-purple-500 to-pink-600 text-white px-8 py-4 rounded-full text-lg font-bold hover:from-purple-600 hover:to-pink-700 transition-all transform hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-arrow-right-line mr-2"></i>
            수업 더 보기
          </button>
        </div>
      </div>
    </section>
  );
};

export default ClassesSection;
